import React from 'react';

/**
 * MarkdownText — lightweight inline markdown renderer (no extra deps).
 * Handles the subset the CIMA agent actually outputs:
 *   **bold**, *italic*, `code`, ## headings,
 *   - / • bullet lists, 1. numbered lists, blank-line paragraphs.
 */

// ── inline spans ──────────────────────────────────────────────────────────
function parseInline(text: string): React.ReactNode[] {
  // Pattern: **bold**, *italic*, `code`
  const parts: React.ReactNode[] = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));

    if (m[2] !== undefined) {
      parts.push(<strong key={m.index}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      parts.push(<em key={m.index}>{m[3]}</em>);
    } else if (m[4] !== undefined) {
      parts.push(
        <code key={m.index} style={{
          background:   'var(--primary-dim)',
          color:        'var(--primary)',
          borderRadius: 3,
          padding:      '1px 5px',
          fontSize:     '0.9em',
          fontFamily:   'var(--font-mono)',
        }}>
          {m[4]}
        </code>,
      );
    }
    last = m.index + m[0].length;
  }

  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ── block-level parser ────────────────────────────────────────────────────
export function MarkdownText({ children, style }: { children: string; style?: React.CSSProperties }) {
  if (!children) return null;

  const lines  = children.split('\n');
  const blocks: React.ReactNode[] = [];
  let   i      = 0;
  let   key    = 0;

  while (i < lines.length) {
    const line = lines[i];

    // ── Blank line — paragraph break ───────────────────────────────────
    if (line.trim() === '') {
      i++;
      continue;
    }

    // ── Heading ## / ### ──────────────────────────────────────────────
    const headingMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const Tag   = level === 1 ? 'h3' : level === 2 ? 'h4' : 'h5';
      blocks.push(
        <Tag key={key++} style={{ margin: '8px 0 4px', fontSize: level === 1 ? 15 : 13, fontWeight: 700, color: 'var(--fg)' }}>
          {parseInline(headingMatch[2])}
        </Tag>,
      );
      i++;
      continue;
    }

    // ── Bullet list ────────────────────────────────────────────────────
    if (/^[\-\*•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[\-\*•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[\-\*•]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={key++} style={{ margin: '4px 0 4px 16px', padding: 0, lineHeight: 1.7 }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ marginBottom: 1 }}>{parseInline(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // ── Numbered list ──────────────────────────────────────────────────
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={key++} style={{ margin: '4px 0 4px 18px', padding: 0, lineHeight: 1.7 }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ marginBottom: 1 }}>{parseInline(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // ── Regular paragraph (accumulate consecutive non-blank lines) ────
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,3}|\d+\.|\s*[\-\*•])\s/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      blocks.push(
        <p key={key++} style={{ margin: '3px 0', lineHeight: 1.65 }}>
          {paraLines.flatMap((pl, pi) => [
            ...parseInline(pl),
            pi < paraLines.length - 1 ? <br key={`br-${pi}`} /> : null,
          ]).filter(Boolean)}
        </p>,
      );
    }
  }

  return (
    <div style={{ fontSize: 14, color: 'var(--fg)', ...style }}>
      {blocks}
    </div>
  );
}
